import { useState } from 'react';
import { useTheme, type Theme } from '../hooks/useTheme';
import { NeuralIcon } from '../components/agent/NeuralIcon';
import { Wordmark } from '../brand/Brand';

export function WelcomePage() {
  const [annual, setAnnual] = useState(true);
  const { theme, setTheme, resolvedTheme } = useTheme();

  const toggleBilling = () => setAnnual((prev) => !prev);

  const cycleTheme = () => {
    const next: Record<Theme, Theme> = { dark: 'light', light: 'system', system: 'dark' };
    setTheme(next[theme]);
  };

  return (
    <div className="welcome-page">
      {/* Nav */}
      <nav className="welcome-nav">
        <Wordmark size={20} href="/" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div className="welcome-nav-links">
            <a href="#features">features</a>
            <a href="#pricing">pricing</a>
            <a href="#how">how it works</a>
          </div>
          <button
            className="welcome-theme-toggle"
            onClick={cycleTheme}
            title={`Theme: ${theme}`}
            aria-label={`Switch theme (currently ${theme})`}
          >
            {theme === 'system' ? '◐' : resolvedTheme === 'dark' ? '☽' : '☀'}
          </button>
          <a href="/login" className="welcome-nav-cta">sign in</a>
        </div>
      </nav>

      {/* Hero */}
      <section className="welcome-hero">
        <div className="welcome-hero-icon">
          <NeuralIcon size={80} />
        </div>
        <div className="welcome-hero-wordmark">
          <Wordmark size={56} />
        </div>
        <div className="welcome-hero-tagline">The repo for everything you&rsquo;ll build next</div>
        <div className="welcome-hero-sub">
          Capture ideas from Claude, Codex, Siri, your terminal, the web, or email.
          AI finds the connections you&rsquo;d miss.
        </div>
        <a href="/login" className="welcome-hero-cta">Get started free</a>
        <a href="#demo" className="welcome-hero-scroll">see it in action ↓</a>
      </section>

      {/* Demo */}
      <section className="welcome-section" id="demo">
        <div className="welcome-section-label">Demo</div>
        <div className="welcome-section-title">Ideas flow in. Connections surface.</div>
        <div className="welcome-section-desc">
          Tell Claude an idea mid-conversation. NeuralRepo tags it, links it to what you already
          have, and lines up duplicates and quiet ideas for your weekly triage.
        </div>
        <div className="welcome-demo-container">
          <div className="welcome-demo-bar">
            <div className="welcome-demo-dot" />
            <div className="welcome-demo-dot" />
            <div className="welcome-demo-dot" />
            <span className="welcome-demo-bar-label">claude → neuralrepo → triage</span>
          </div>
          <video
            className="welcome-demo-video"
            src="/demo/neuralrepo-2.mp4"
            poster="/demo/neuralrepo-2-poster.jpg"
            width={1280}
            height={800}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        </div>
        <div className="welcome-sources">
          <span className="welcome-source-tag pro">web app</span>
          <span className="welcome-source-tag pro">claude mcp</span>
          <span className="welcome-source-tag pro">claude code skill</span>
          <span className="welcome-source-tag pro">codex cli</span>
          <span className="welcome-source-tag pro">nrepo cli</span>
          <span className="welcome-source-tag pro">siri shortcuts</span>
          <span className="welcome-source-tag pro">email capture</span>
        </div>
      </section>

      {/* How it works */}
      <section className="welcome-section" id="how">
        <div className="welcome-section-label">How it works</div>
        <div className="welcome-section-title">Three steps. Zero friction.</div>
        <div className="welcome-steps">
          <div className="welcome-step">
            <div className="welcome-step-num">01</div>
            <div className="welcome-step-title">Capture</div>
            <div className="welcome-step-desc">Say it, type it, or let Claude save it. Ideas arrive from wherever you think — terminal, conversation, Siri, email.</div>
          </div>
          <div className="welcome-step">
            <div className="welcome-step-num">02</div>
            <div className="welcome-step-title">Connect</div>
            <div className="welcome-step-desc">Vector embeddings find related ideas and detect duplicates automatically. Auto-tags and kanban keep things organized without effort.</div>
          </div>
          <div className="welcome-step">
            <div className="welcome-step-num">03</div>
            <div className="welcome-step-title">Build</div>
            <div className="welcome-step-desc">Search by meaning, explore connections in mind maps, and export context to your code editor when it's time to ship.</div>
          </div>
        </div>

        <div className="welcome-terminal">
          <div className="welcome-terminal-bar">
            <div className="welcome-demo-dot" />
            <div className="welcome-demo-dot" />
            <div className="welcome-demo-dot" />
          </div>
          <div className="welcome-terminal-body">
            <div><span className="t-prompt">%</span> <span className="t-cmd">nrepo push</span> <span className="t-str">"Use vector similarity for duplicate detection"</span></div>
            <div><span className="t-ok">✓ Idea captured</span></div>
            <div><span className="t-out">#47 captured &nbsp; ⬡ Use vector similarity for duplicate detection</span></div>
            <div style={{ marginTop: 8 }}><span className="t-prompt">%</span> <span className="t-cmd">nrepo search</span> <span className="t-str">"that dedup idea"</span></div>
            <div><span className="t-out">Search: &ldquo;that dedup idea&rdquo; (semantic) &mdash; 3 results</span></div>
            <div style={{ marginTop: 4 }}><span className="t-out">#47 captured &nbsp; ⬡</span> <span className="t-cmd">Use vector similarity for duplicate detection</span> <span className="t-out">(94%)</span></div>
            <div><span className="t-out">#31 exploring ◈</span> <span className="t-cmd">Cosine distance threshold for related ideas</span> <span className="t-out">(87%)</span></div>
            <div><span className="t-out">#12 shipped &nbsp; ◎</span> <span className="t-cmd">Exact title match dedup — too many false negatives</span> <span className="t-out">(72%)</span></div>
          </div>
        </div>
      </section>

      {/* Feature matrix + Pricing */}
      <section className="welcome-section" id="features">
        <div className="welcome-section-label">Compare plans</div>
        <div className="welcome-section-title" id="pricing">Free to start. Pro when you're ready.</div>
        <div className="welcome-section-desc">
          Every account starts with 30 days of full Pro access &mdash; no credit card. After that, keep Free or upgrade.
        </div>

        <table className="welcome-matrix">
          <thead>
            <tr>
              <th style={{ width: '55%' }}>Feature</th>
              <th>Free</th>
              <th className="welcome-matrix-pro-head">Pro</th>
            </tr>
          </thead>
          <tbody>
            <tr className="welcome-matrix-category"><td colSpan={3}>Storage</td></tr>
            <tr><td>Ideas</td><td className="lm">50</td><td className="ck">unlimited</td></tr>

            <tr className="welcome-matrix-category"><td colSpan={3}>Capture</td></tr>
            <tr><td>Web app</td><td className="ck"><span className="ck-dot">●</span></td><td className="ck"><span className="ck-dot">●</span></td></tr>
            <tr><td>Claude MCP connector</td><td className="ck"><span className="ck-dot">●</span></td><td className="ck"><span className="ck-dot">●</span></td></tr>
            <tr><td>Claude Code skill</td><td className="ck"><span className="ck-dot">●</span></td><td className="ck"><span className="ck-dot">●</span></td></tr>
            <tr><td>nrepo CLI</td><td className="ck"><span className="ck-dot">●</span></td><td className="ck"><span className="ck-dot">●</span></td></tr>
            <tr><td>Siri Shortcuts</td><td className="ds">&mdash;</td><td className="ck"><span className="ck-dot">●</span></td></tr>
            <tr><td>Email capture</td><td className="ds">&mdash;</td><td className="ck"><span className="ck-dot">●</span></td></tr>

            <tr className="welcome-matrix-category"><td colSpan={3}>Search &amp; AI</td></tr>
            <tr><td>Keyword search</td><td className="ck"><span className="ck-dot">●</span></td><td className="ck"><span className="ck-dot">●</span></td></tr>
            <tr><td>Semantic search</td><td className="lm">10/mo</td><td className="ck">unlimited</td></tr>
            <tr><td>Duplicate detection</td><td className="ds">&mdash;</td><td className="ck"><span className="ck-dot">●</span></td></tr>
            <tr><td>Auto-tagging</td><td className="lm">basic</td><td className="ck">full</td></tr>

            <tr className="welcome-matrix-category"><td colSpan={3}>Organize &amp; visualize</td></tr>
            <tr><td>Kanban board</td><td className="ck"><span className="ck-dot">●</span></td><td className="ck"><span className="ck-dot">●</span></td></tr>
            <tr><td>Idea relations &amp; linking</td><td className="lm">view</td><td className="ck">create</td></tr>
            <tr><td>Mind map</td><td className="lm">view</td><td className="ck">full</td></tr>

            <tr className="welcome-matrix-category"><td colSpan={3}>Export &amp; integrate</td></tr>
            <tr><td>JSON / CSV export</td><td className="ck"><span className="ck-dot">●</span></td><td className="ck"><span className="ck-dot">●</span></td></tr>
            <tr><td>BYOK (your own API keys)</td><td className="ds">&mdash;</td><td className="ck"><span className="ck-dot">●</span></td></tr>
            <tr><td>API access</td><td className="ds">&mdash;</td><td className="ck"><span className="ck-dot">●</span></td></tr>
          </tbody>
        </table>

        {/* Billing toggle */}
        <div className="welcome-toggle-wrap">
          <span className={`welcome-toggle-label${!annual ? ' active' : ''}`} onClick={toggleBilling}>Monthly</span>
          <div className={`welcome-toggle${annual ? ' on' : ''}`} onClick={toggleBilling}>
            <div className="welcome-toggle-knob" />
          </div>
          <span className={`welcome-toggle-label${annual ? ' active' : ''}`} onClick={toggleBilling}>
            Annual <span style={{ color: 'var(--accent)', opacity: 0.6 }}>save $14</span>
          </span>
        </div>

        {/* Pricing row */}
        <div className="welcome-pricing-row">
          <div style={{ textAlign: 'center' }}>
            <div className="welcome-pricing-label">Free</div>
            <div className="welcome-pricing-amount">$0</div>
            <div className="welcome-pricing-period">forever</div>
            <div style={{ marginTop: 14 }}>
              <a href="/login" className="welcome-btn-ghost">Get started</a>
            </div>
          </div>
          <div className="welcome-pricing-divider" />
          <div style={{ textAlign: 'center' }}>
            <div className="welcome-pricing-label pro">Pro</div>
            <div className="welcome-pricing-amount">{annual ? '$70' : '$7'}</div>
            <div className="welcome-pricing-period">{annual ? '/year' : '/month'}</div>
            <div className="welcome-pricing-savings">
              {annual ? "that's $5.83/mo" : '$84/yr if monthly'}
            </div>
            <div style={{ marginTop: 14 }}>
              <a href="/login" className="welcome-hero-cta">Try Pro free for 30 days</a>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="welcome-stats">
          <div className="welcome-stats-item">
            <div className="welcome-stats-number">100%</div>
            <div className="welcome-stats-label">Cloudflare edge</div>
          </div>
          <div className="welcome-stats-divider" />
          <div className="welcome-stats-item">
            <div className="welcome-stats-number">&lt;50ms</div>
            <div className="welcome-stats-label">API latency</div>
          </div>
          <div className="welcome-stats-divider" />
          <div className="welcome-stats-item">
            <div className="welcome-stats-number">Open</div>
            <div className="welcome-stats-label">CLI source</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="welcome-footer">
        <div className="welcome-footer-left">
          NeuralRepo is operated by NeuralConfig LLC<br />
          © 2026 NeuralConfig LLC
        </div>
        <div className="welcome-footer-links">
          <a href="https://neuralconfig.com/terms/">terms</a>
          <a href="https://neuralconfig.com/privacy/neuralrepo/">privacy</a>
          <a href="https://github.com/neuralconfig/nrepo">github</a>
        </div>
      </footer>
    </div>
  );
}
